import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCurrentRestaurant } from "@/hooks/useCurrentRestaurant";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { BusinessType, Restaurant } from "@/model/entities";
import { uploadImage } from "@/service/firebaseUtils";
import UploadImageInput from "@/components/input/UploadImageInput";
import UploadImagePreview from "@/components/input/UploadImagePreview";

const initialRestaurantData: Partial<Restaurant> = {
  restaurantName: "",
  phone: "",
  address: "",
  businessType: BusinessType.OTHER,
  facebook: "",
  instagram: "",
  waze: "",
  whatsapp: "",
};

const ProfilePage = () => {
  const { t } = useTranslation();
  const { restaurant, isLoading: restaurantLoading, error, user, updateRestaurant } = useCurrentRestaurant();  
  const [loading, setLoading] = useState(false);
  const [restaurantData, setRestaurantData] = useState<Partial<Restaurant>>(
    restaurant ? restaurant : initialRestaurantData
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  useEffect(() => {
    if (restaurant) {
      setRestaurantData({
        restaurantName: restaurant.restaurantName || "",
        phone: restaurant.phone || "",
        address: restaurant.address || "",
        businessType: (restaurant.businessType || "other") as BusinessType,
        facebook: restaurant.facebook || "",
        instagram: restaurant.instagram || "",
        waze: restaurant.waze || "",
        whatsapp: restaurant.whatsapp || "",
      });
      console.log("Restaurant logo: ", restaurant.restaurantLogo);
      if (restaurant.restaurantLogo) {
        console.log("Setting logo preview from restaurant data");
        setLogoPreview(restaurant.restaurantLogo);
      } else {
        setLogoPreview(null);
      }
    }
  }, [restaurant]);

  const handleRestaurantInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setRestaurantData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBusinessTypeChange = (value: BusinessType) => {
    setRestaurantData((prev) => ({
      ...prev,
      businessType: value,
    }));
  };

  const handleLogoChange = (file: File | null) => {
    setLogoFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoPreview(null);
    }
  };

  const handleRestaurantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!restaurant || !user?.uid) return;
    setLoading(true);
    try {
      let logoUrl = restaurant.restaurantLogo || "";
      if (logoFile) {
        const path = `restaurants/${restaurant.id}/logo`;
        logoUrl = await uploadImage(logoFile, path);
      }
      // Use react-query mutation from useCurrentRestaurant
      if (typeof updateRestaurant !== "undefined") {
        await updateRestaurant.mutateAsync({
          ...restaurant,
          ...restaurantData,
          restaurantLogo: logoUrl,
        });
        toast.success(t("profile.saveSuccess"));
      }
    } catch (error) {
      console.error("Error updating restaurant:", error);
      toast.error(t("profile.saveError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      {/* New Plan Section */}
      <Card className="mb-6">
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <span className="font-semibold">{t("profile.currentPlan")}:</span>{" "}
              <span>
                {restaurant?.plan
                  ? t(`plans.${restaurant.plan}`)
                  : t("profile.noPlan")}
              </span>
            </div>
          </div>
          <div className="mt-2 text-sm text-muted-foreground">
            {t("profile.upgradeContactAdmin")}
          </div>
        </CardContent>
      </Card>
      {/* Existing Restaurant Section */}
      <Card>
        <CardHeader>
          <CardTitle>{t("profile.restaurantSection")}</CardTitle>
          <CardDescription>
            {t("profile.restaurantSectionDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {restaurantLoading ? (
            <div className="flex justify-center items-center h-32">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("profile.loading")}
            </div>
          ) : error ? (
            <div className="text-red-500">{t("profile.loadError")}</div>
          ) : (
            <form onSubmit={handleRestaurantSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">{t("profile.restaurantName")}</Label>
                  <Input
                    id="name"
                    name="name"
                    value={restaurantData.restaurantName}
                    onChange={handleRestaurantInputChange}
                    readOnly
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="businessType">
                    {t("profile.businessType")}
                  </Label>
                  <Select
                    value={restaurantData.businessType}
                    onValueChange={handleBusinessTypeChange}
                    disabled
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t("profile.selectBusinessType")}> 
                        {restaurantData.businessType
                          ? t(`businessTypes.${restaurantData.businessType}`)
                          : null}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(BusinessType).map((type) => (
                        <SelectItem key={type} value={type}>
                          {t(`businessTypes.${type}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">{t("profile.restaurantPhone")}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={restaurantData.phone}
                    onChange={handleRestaurantInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">
                    {t("profile.restaurantAddress")}
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={restaurantData.address}
                    onChange={handleRestaurantInputChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="logo">{t("profile.restaurantLogo")}</Label>
                  <UploadImageInput onFileChange={handleLogoChange} />
                  <UploadImagePreview
                    currentImageUrl={logoPreview ?? restaurant?.restaurantLogo ?? undefined}
                    newImageFile={logoFile}
                  />
                </div>
              </div>
              
              {/* Social Links Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">{t("profile.socialLinks")}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="instagram">{t("profile.instagram")}</Label>
                    <Input
                      id="instagram"
                      name="instagram"
                      placeholder="https://instagram.com/username"
                      value={restaurantData.instagram}
                      onChange={handleRestaurantInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="facebook">{t("profile.facebook")}</Label>
                    <Input
                      id="facebook"
                      name="facebook"
                      placeholder="https://facebook.com/page"
                      value={restaurantData.facebook}
                      onChange={handleRestaurantInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="whatsapp">{t("profile.whatsapp")}</Label>
                    <Input
                      id="whatsapp"
                      name="whatsapp"
                      placeholder="https://wa.me/1234567890"
                      value={restaurantData.whatsapp}
                      onChange={handleRestaurantInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="waze">{t("profile.waze")}</Label>
                    <Input
                      id="waze"
                      name="waze"
                      placeholder="https://waze.com/ul/hsv..."
                      value={restaurantData.waze}
                      onChange={handleRestaurantInputChange}
                    />
                  </div>
                </div>
              </div>
              
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("profile.saving")}
                  </>
                ) : (
                  t("profile.save")
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProfilePage;
